import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Circle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import LogoutButton from "@/components/LogoutButton";

const AUTOSAVE_MS = 10000;

function wordCount(text) {
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

function relativeSaved(ts) {
  if (!ts) return "";
  const s = Math.max(1, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `Saved ${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `Saved ${m}m ago`;
  return `Saved ${new Date(ts).toLocaleTimeString()}`;
}

export default function Editor() {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [_tick, setTick] = useState(0); // to refresh "saved Xs ago"

  const savedSnapshotRef = useRef({ title: "", body: "" });
  const inFlightRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/drafts/${draftId}`);
        if (!mounted) return;
        setTitle(data.title || "");
        setBody(data.body || "");
        savedSnapshotRef.current = { title: data.title || "", body: data.body || "" };
        setLastSavedAt(data.updated_at ? new Date(data.updated_at).getTime() : null);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/app/dashboard", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [draftId, navigate]);

  // dirty detection
  useEffect(() => {
    if (loading) return;
    const snap = savedSnapshotRef.current;
    setDirty(title !== snap.title || body !== snap.body);
  }, [title, body, loading]);

  const save = useCallback(
    async (opts = {}) => {
      if (inFlightRef.current) return;
      const snap = savedSnapshotRef.current;
      if (!opts.force && title === snap.title && body === snap.body) return;
      inFlightRef.current = true;
      setSaving(true);
      try {
        const { data } = await api.put(`/drafts/${draftId}`, { title, body });
        savedSnapshotRef.current = { title: data.title || "", body: data.body || "" };
        setLastSavedAt(Date.now());
        setDirty(false);
        if (opts.manual) toast.success("Draft saved.");
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setSaving(false);
        inFlightRef.current = false;
      }
    },
    [title, body, draftId],
  );

  // Autosave every 10s
  useEffect(() => {
    if (loading) return;
    const id = setInterval(() => {
      save();
    }, AUTOSAVE_MS);
    return () => clearInterval(id);
  }, [save, loading]);

  // Tick to refresh "saved Xs ago"
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 5000);
    return () => clearInterval(t);
  }, []);

  // Save on unmount / tab hide
  useEffect(() => {
    const onHide = () => save();
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("beforeunload", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("beforeunload", onHide);
      save();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save]);

  const statusLabel = useMemo(() => {
    if (saving) return { text: "Autosaving…", icon: Loader2, spin: true, tone: "text-amber-300" };
    if (dirty) return { text: "Unsaved changes", icon: Circle, tone: "text-amber-300/70" };
    if (lastSavedAt) return { text: relativeSaved(lastSavedAt), icon: CheckCircle2, tone: "text-emerald-300/80" };
    return { text: "Draft ready", icon: Circle, tone: "text-stone-500" };
  }, [saving, dirty, lastSavedAt]);

  const backToDashboard = async () => {
    await save();
    navigate("/app/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-950 grid place-items-center">
        <div className="font-mono-mini text-xs uppercase tracking-[0.35em] text-amber-200/60">
          Opening your page…
        </div>
      </div>
    );
  }

  const StatusIcon = statusLabel.icon;

  return (
    <div
      className="min-h-screen text-stone-100 grain bg-cover bg-top bg-no-repeat bg-fixed"
      style={{
        backgroundImage:
          "url('https://customer-assets-v7afamib.emergentagent.net/wingman/4b71e223-37e4-4615-8453-e68a2ac08d9e/attachments/c731fa18b486474686e3c5125b67c3a9_ChatGPT%20Image%20Sep%204%2C%202026%2C%2004_50_11%20PM.png')",
      }}
      data-testid="editor-root"
    >
      <header className="sticky top-0 z-20 border-b border-stone-900/60 bg-stone-950/70 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-6 py-4">
          <button
            data-testid="editor-back-btn"
            onClick={backToDashboard}
            className="inline-flex items-center gap-2 text-stone-300 hover:text-amber-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to your room</span>
          </button>
          <div className="flex items-center gap-4">
            <div
              data-testid="draft-status-indicator"
              className={`hidden sm:flex items-center gap-2 font-mono-mini text-[11px] uppercase tracking-[0.24em] ${statusLabel.tone}`}
            >
              <StatusIcon className={`w-3.5 h-3.5 ${statusLabel.spin ? "animate-spin" : ""}`} />
              {statusLabel.text}
            </div>
            <button
              onClick={() => save({ manual: true, force: true })}
              disabled={saving}
              data-testid="draft-save-btn"
              className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-stone-950 font-semibold text-sm px-4 py-2 transition-all"
            >
              <Save className="w-4 h-4" />
              Save draft
            </button>
            <LogoutButton variant="icon" />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <div
          className={`rounded-3xl p-8 sm:p-10 transition-all duration-500 ${
            title.trim() || body.trim()
              ? "bg-stone-950/70 backdrop-blur-md border border-stone-800/60 shadow-2xl"
              : "bg-transparent border border-transparent"
          }`}
        >
          <input
            data-testid="draft-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Give it a title… or don’t."
            style={{ textShadow: "0 2px 12px rgba(0,0,0,0.85), 0 0 4px rgba(0,0,0,0.7)" }}
            className="w-full bg-transparent border-none outline-none font-serif-editorial text-4xl sm:text-5xl font-semibold leading-tight text-white placeholder:text-stone-300/70 focus:placeholder:text-stone-300/50 mb-8"
          />

          <textarea
            data-testid="draft-body-textarea"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Start with whatever you can’t stop thinking about tonight…"
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.85), 0 0 3px rgba(0,0,0,0.7)" }}
            className="w-full min-h-[55vh] bg-transparent border-none outline-none resize-none font-serif-editorial text-xl leading-relaxed text-white placeholder:text-white/80"
          />

          <div className="mt-8 flex items-center justify-between border-t border-stone-800 pt-6 text-xs font-mono-mini text-stone-400 uppercase tracking-[0.22em]">
            <span data-testid="draft-word-count">{wordCount(body)} words</span>
            <span className="sm:hidden inline-flex items-center gap-2">
              <StatusIcon className={`w-3.5 h-3.5 ${statusLabel.spin ? "animate-spin" : ""}`} />
              {statusLabel.text}
            </span>
            <span>Autosaving every 10s</span>
          </div>
        </div>
      </main>
    </div>
  );
}
